// Closing a project closes what's left in it (Lj, Oct 11: "When we mark a project completed it should mark the tasks
// as completed" — refined the same day: the unfinished ones are VOIDED, not done).
//
// A void task (`status: 'void'`) is a terminal status next to done:
//   - it leaves every list, plan, board, card stack, Needs you and count, like a done task;
//   - it is NOT finished work: not Done today, not task-count / shipped / subtask goals, not the plan hit rate, not
//     post-mortem efficiency (none of those count anything but `status === 'done'`);
//   - it pays nothing: share pay, piece pay and the payroll feed skip it (`isVoidTask` — sharepay / the books call it);
//   - time already clocked on it stays real time (it was worked) and shows as "on a voided task".
// The app keeps void tasks out of its task list at the source (TaskContext: `tasks` = live, `voidTasks` /
// `tasksWithVoid` for the time views, the Daily Log, the task page and the Tag page); the MCP mirror does the same.
//
// Fields on a voided task: status 'void', voidedAt (ms), voidedWith (the project id that closed it), voidedFrom (the
// status it had — todo / paused / blocked — so a restore puts it back exactly). Restoring clears all three.
// Cancelled projects void nothing: their tasks are left as they are (the record of what was open when it was called off).
import { update } from './commands/writes.js';
import { localDateStr } from './time.js';
import { canonTag, resolveTag, tagIndex } from './tags.js';

export const VOID = 'void';
export const isVoidTask = (t) => !!t && t.status === VOID;
/** The task list without void tasks (the same array back when there are none, so memos hold). */
export function liveTasks(tasks) {
  const list = tasks || [];
  return list.some(isVoidTask) ? list.filter((t) => !isVoidTask(t)) : list;
}
/** Not done, not void, not deleted — what a project close voids. */
export const isUnfinished = (t) => !!t && t.status !== 'done' && t.status !== VOID && !t.deletedAt;

/** Every task of the project (its own + subtasks of those, whatever projectId a subtask carries), buckets included. */
export function projectTaskIds(tasks, projectId) {
  const list = tasks || [];
  const ids = new Set(list.filter((t) => t && t.projectId === projectId).map((t) => t.id));
  let grew = true;
  while (grew) {   // subtasks follow their parent even if their own projectId is stale or missing
    grew = false;
    for (const t of list) if (t && t.parentId && ids.has(t.parentId) && !ids.has(t.id)) { ids.add(t.id); grew = true; }
  }
  return ids;
}

/** The task the timer is on, when it belongs to the project (and is still open), else null. */
export function runningInProject(ctx, projectId) {
  const id = ctx.timer?.taskId || null;
  if (!id) return null;
  const t = (ctx.tasks || []).find((x) => x.id === id);
  return t && isUnfinished(t) && projectTaskIds(ctx.tasks, projectId).has(id) ? t : null;
}

/** What closing the project will do: { open, running: task | null, tasks } — for the confirm. */
export function closePreview(ctx, projectId) {
  const ids = projectTaskIds(ctx.tasks, projectId);
  const open = (ctx.tasks || []).filter((t) => ids.has(t.id) && isUnfinished(t));
  return { open: open.length, running: runningInProject(ctx, projectId), tasks: open };
}

/** The tasks a project close voided (restorable on reopen). */
export function voidedWith(tasks, projectId) {
  return (tasks || []).filter((t) => isVoidTask(t) && t.voidedWith === projectId);
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The confirm's words. `preview` = closePreview(...). */
export function closeConfirmText(project, preview) {
  const name = project?.name || 'this project';
  const n = preview?.open || 0;
  const title = `Complete ${name}?`;
  if (!n) return { title, message: 'Every task in it is finished.', confirmLabel: 'Complete' };
  let message = `${plural(n, 'unfinished task')} will be voided — hidden, not counted as done, nothing paid on them.`;
  if (preview.running) message += ` 1 is running; its time is banked first.`;
  return { title, message, confirmLabel: `Complete · void ${n}` };
}

/** Reopening a completed project: what the second button offers ({ count, label } or null when nothing was voided). */
export function reopenOffer(project, tasks) {
  const n = voidedWith(tasks, project?.id).length;
  return n ? { count: n, label: `Restore the ${plural(n, 'voided task')}` } : null;
}

/** The patch that voids one task. */
export function voidPatch(task, projectId, at) {
  return { status: VOID, voidedAt: at, voidedWith: projectId || null, voidedFrom: task.status && task.status !== VOID ? task.status : 'todo', updatedAt: at };
}
/** The patch that brings a void task back to what it was. */
export function restorePatch(task, at) {
  const back = task.voidedFrom && task.voidedFrom !== VOID && task.voidedFrom !== 'done' ? task.voidedFrom : 'todo';
  return { status: back, voidedAt: null, voidedWith: null, voidedFrom: null, updatedAt: at };
}

/**
 * Restore void tasks by hand (the task page): the task, its void subtasks, and its void parents (so it has somewhere to
 * show). Pure writes; `ctx.tasks` must include the void ones.
 */
export function restoreTaskWrites(ctx, { id, at }) {
  const tasks = ctx.tasks || [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const t = byId.get(id);
  if (!t) throw new Error(`no task ${id}`);
  if (!isVoidTask(t)) throw new Error(`${t.title || 'That task'} is not voided`);
  const ids = new Set([id]);
  for (let p = byId.get(t.parentId); p && !ids.has(p.id); p = byId.get(p.parentId)) if (isVoidTask(p)) ids.add(p.id);
  const walk = (pid) => tasks.forEach((k) => { if (k.parentId === pid && !ids.has(k.id) && isVoidTask(k)) { ids.add(k.id); walk(k.id); } });
  walk(id);
  return { writes: [...ids].map((x) => update(`tasks/${x}`, restorePatch(byId.get(x), at))), restored: ids.size };
}

/** "12 tasks voided with The Poly Palace" */
export const voidLine = (count, name) => `${plural(count, 'task')} voided with ${name || 'a project'}`;

/**
 * The Daily Log's line(s) for a day: tasks voided that day, one row per project.
 * → [{ projectId, name, count, line }]
 */
export function voidedOn(tasks, projects, date) {
  const by = new Map();
  for (const t of tasks || []) {
    if (!isVoidTask(t) || !t.voidedAt || localDateStr(t.voidedAt) !== date) continue;
    const k = t.voidedWith || t.projectId || 'none';
    by.set(k, (by.get(k) || 0) + 1);
  }
  const pname = new Map((projects || []).map((p) => [p.id, p.name]));
  return [...by.entries()].map(([projectId, count]) => {
    const name = pname.get(projectId) || null;
    return { projectId: projectId === 'none' ? null : projectId, name, count, line: voidLine(count, name) };
  }).sort((a, b) => b.count - a.count);
}

/**
 * The void tasks carrying a tag (the Tag pages list them apart, with a VOID badge — never in open / done or the counts).
 * `slug` = the page's tag slug; `tags` = the suite list or its index. Newest voided first.
 */
export function voidedTagged(slug, voidTasks, tags) {
  if (!slug) return [];
  const idx = tags instanceof Map ? tags : tagIndex(tags || []);
  const has = (list) => (Array.isArray(list) ? list : []).some((t) => t && canonTag(resolveTag(t, idx)) === slug);
  return (voidTasks || []).filter((t) => isVoidTask(t) && !t.deletedAt && has(t.tags)).sort((a, b) => (b.voidedAt || 0) - (a.voidedAt || 0));
}
